import React, { useState, useEffect } from 'react';
import { Button, Table, Spin } from 'antd';
import type { ColumnsType } from 'antd/es/table'; // Import the correct type
import './FinalSummary.css';
import * as XLSX from 'xlsx';
import GetTableHeader from '../../../app/tableHeaders';
import { getFinalResultCost } from 'src/app/query.service';

interface DataType {
  [key: string]: any;
}

interface FinalSummaryProps {
  onBack: () => void;
  queryType: string;
  propData: DataType;
  filename: string;
}

const FinalSummary: React.FC<FinalSummaryProps> = ({ onBack, queryType, propData, filename }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<DataType[]>([]);
  const [tableHeader, setTableHeader] = useState<ColumnsType<DataType>>([]); // Use ColumnsType<DataType>
  const [isDownloading, setIsDownloading] = useState(false);

  const fetchQueryTableData = async () => {
    setIsLoading(true);
    try {
      const dataType: DataType[] = await getFinalResultCost<DataType>(queryType, 'final', propData);
      setResult(dataType);
    } catch (error) {
      console.error('Error fetching table data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const column = GetTableHeader(queryType) as ColumnsType<DataType>; // Explicitly cast the result
    setTableHeader(column);
    fetchQueryTableData().catch((error) =>
      console.error('Error in useEffect when fetching data:', error)
    );
  }, [queryType]);

  const downloadAsCsv = async () => {
    const token = localStorage.getItem('id_token');
    if (!token) {
      console.error('No auth token found!');
      return;
    }
    setIsDownloading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/${queryType}/query/final/export`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(propData),
        }
      );

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading CSV:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div>
      <div style={{ width: '100%' }}>
        <Button onClick={onBack} className="ConcernQuery--Buttons">
          Back To Query
        </Button>
        <Button
          onClick={downloadAsCsv}
          className="ConcernQuery--Buttons"
          style={{ float: 'right' }}
          loading={isDownloading}
          disabled={isDownloading}
        >
          Download as CSV
        </Button>
      </div>
      <Table
        loading={{
          spinning: isLoading,
          indicator: <Spin className="loader-color" />,
        }}
        columns={tableHeader}
        dataSource={result}
        scroll={{ x: 'max-content', y: 1000 }}
        size="middle"
        bordered
      />
    </div>
  );
};

export default FinalSummary;
